import type { ImageItem, Collection } from '~/types';
export function toTitleCase(str: string): string {
  return str
    .replace(/-|_/g, ' ') // Replace hyphens and underscores with spaces
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function getCollectionAndName(file: File): { collection: string; name: string } {
  const fullPath = file.webkitRelativePath || (file as any).path || '';
  const pathParts = fullPath.split('/').filter(Boolean);

  let collection = '';
  let name = file.name;

  if (pathParts.length > 1) {
    // Take all parts except the last one (filename) for the collection path
    collection = pathParts.slice(0, -1).join('/');
    name = pathParts[pathParts.length - 1].split('.')[0];
  } else {
    name = file.name.split('.')[0];
  }

  if (collection === '.') {
    collection = '';
  }

  return { collection, name };
}

export function groupContent(images: any[], currentCollection: string | null) {
  return images.reduce(
    (
      acc: {
        folders: { name: string; path: string; count: number; id: string; isApiCollection?: boolean }[];
        images: any;
      },
      img
    ) => {
      if (!img.collection) {
        acc.images.push(img);
        return acc;
      }

      if (currentCollection) {
        // If we're in a collection, only show direct children
        const relativePath = img.collection.slice(currentCollection.length + 1);
        if (!relativePath) {
          // This image is directly in the current collection
          acc.images.push(img);
        } else {
          // This image is in a subfolder
          const topLevelFolder = relativePath.split('/')[0];
          const folderPath = `${currentCollection}/${topLevelFolder}`;

          const existingFolder = acc.folders.find((f) => f.path === folderPath);
          if (existingFolder) {
            existingFolder.count++;
          } else {
            acc.folders.push({
              name: topLevelFolder,
              path: folderPath,
              count: 1,
              id: folderPath,
              isApiCollection: false,
            });
          }
        }
      } else {
        // At root level, show top-level folders
        const topLevelFolder = img.collection.split('/')[0];
        const existingFolder = acc.folders.find((f) => f.path === topLevelFolder);
        if (existingFolder) {
          existingFolder.count++;
        } else {
          acc.folders.push({
            name: topLevelFolder,
            path: topLevelFolder,
            count: 1,
            id: topLevelFolder,
            isApiCollection: false,
          });
        }
      }

      return acc;
    },
    { folders: [], images: [] }
  );
}

export function getColumnCount(element: HTMLElement | null): number {
  if (!element) return 5; // default column count

  const style = window.getComputedStyle(element);
  const gridTemplateColumns = style.getPropertyValue('grid-template-columns');
  return gridTemplateColumns.split(' ').length;
}

// Helper function to ensure path has a leading slash
export function ensureLeadingSlash(path: string): string {
  return path.startsWith('/') ? path : `/${path}`;
}

// Helper function to get image's title for sorting
export function getImageTitle(img: any): string {
  return (img.title || img.fileMeta?.raw?.name || '').toLowerCase();
}

// Interface for collection mappings
export interface CollectionMappings {
  idToPath: Record<string, string>;
  pathToId: Record<string, string>;
}

// Define the structure for the collection tree
export interface CollectionTreeNode {
  count: number;
  children: { [key: string]: CollectionTreeNode };
  path: string;
  id: string;
  /** Up to six preview tiles (`Collection.nestedImages(limit: 6)`), not the folder contents. */
  images: ImageItem[];
}

export type CollectionTree = Record<string, CollectionTreeNode>;

export function createCollectionMappings(collections: Collection[]): CollectionMappings {
  return collections.reduce(
    (mappings, collection) => {
      const normalizedPath = ensureLeadingSlash(collection.path);
      mappings.idToPath[collection.id] = normalizedPath;
      mappings.pathToId[normalizedPath] = collection.id;
      return mappings;
    },
    { idToPath: {}, pathToId: {} } as CollectionMappings
  );
}

export function sortCollectionKeys(collections: Collection[], keys: string[]): string[] {
  const keyToNameMap = new Map<string, string>();
  for (const collection of collections) {
    const normalizedPath = ensureLeadingSlash(collection.path);
    const parts = normalizedPath.split('/').filter(Boolean);
    if (parts.length > 0) {
      const key = parts[parts.length - 1];
      if (keys.includes(key)) {
        keyToNameMap.set(key, collection.name);
      }
    }
  }
  return [...keys].sort((a, b) => {
    const nameA = keyToNameMap.get(a);
    const nameB = keyToNameMap.get(b);
    if (nameA && nameB) return nameA.localeCompare(nameB);
    return a.localeCompare(b);
  });
}

export function sortImages(
  images: ImageItem[],
  criteria: 'createdAt' | 'title' | 'fileName',
  direction: 'asc' | 'desc'
): ImageItem[] {
  const dir = direction === 'asc' ? 1 : -1;

  return [...images].sort((a, b) => {
    // Check if either image is currently uploading or processing
    const isRecentA = a.status === 'uploading' || a.status === 'processing' || a.id.startsWith('client-');
    const isRecentB = b.status === 'uploading' || b.status === 'processing' || b.id.startsWith('client-');

    // If both are recent uploads (uploading or processing), maintain their upload order using id
    if (isRecentA && isRecentB) {
      // For client IDs, use the order number
      const orderA = a.id.startsWith('client-') ? parseInt(a.id.split('-')[2], 10) : parseInt(a.id.split('-')[1], 10);
      const orderB = b.id.startsWith('client-') ? parseInt(b.id.split('-')[2], 10) : parseInt(b.id.split('-')[1], 10);
      return orderA - orderB;
    }

    // If only one is a recent upload, it should appear at the top
    if (isRecentA) return -1;
    if (isRecentB) return 1;

    // For non-uploading/processing images, proceed with normal sorting
    let valA: any;
    let valB: any;

    switch (criteria) {
      case 'createdAt': {
        // Compare timestamps
        const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        valA = timeA;
        valB = timeB;
        break;
      }

      case 'title': {
        valA = (a.title || '').toLowerCase();
        valB = (b.title || '').toLowerCase();
        break;
      }

      case 'fileName': {
        valA = (a.fileName || a.fileMeta?.fileName || '').toLowerCase();
        valB = (b.fileName || b.fileMeta?.fileName || '').toLowerCase();
        break;
      }

      default:
        return 0;
    }

    if (valA < valB) return -1 * dir;
    if (valA > valB) return 1 * dir;
    return 0;
  });
}

type CountedCollection = Collection & {
  imageCount?: number | null;
  nestedImages?: ImageItem[] | string[] | null;
};

function previews(collection: CountedCollection | undefined): ImageItem[] {
  const images: unknown[] = collection?.nestedImages ?? [];
  return images.filter((image): image is ImageItem => typeof image === 'object' && image !== null);
}

/**
 * Nested folder tree from the flat collections list. Counts and preview tiles come from
 * the server (`Collection.imageCount(nested: true)` / `nestedImages(limit: 6)`), never
 * from loaded grid rows, so they hold for images the grid has not paged in yet - and
 * they do not change with the tag filter.
 */
export function buildCollectionTree(collections: Collection[], pathToId: Record<string, string>): CollectionTree {
  const byId = new Map((collections as CountedCollection[]).map((collection) => [collection.id, collection]));
  const tree: CollectionTree = {};

  for (const collection of collections) {
    const parts = ensureLeadingSlash(collection.path).split('/').filter(Boolean);
    let current = tree;
    let path = '';
    parts.forEach((part: string) => {
      path = path ? `${path}/${part}` : `/${part}`;
      if (!current[part]) {
        const nodeId = pathToId[path] || '';
        const node = byId.get(nodeId);
        current[part] = { count: node?.imageCount ?? 0, children: {}, path, id: nodeId, images: previews(node) };
      }
      current = current[part].children;
    });
  }

  return sortTreeLevel(tree);
}

function sortTreeLevel(node: CollectionTree): CollectionTree {
  const keys = Object.keys(node).sort((a, b) => node[a].path.localeCompare(node[b].path));

  return keys.reduce<CollectionTree>((memo, key) => {
    memo[key] = { ...node[key], children: sortTreeLevel(node[key].children) };
    return memo;
  }, {});
}
